import { Modal } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";

/**
 * Reusable delete confirmation modal component
 *
 * @example
 * ```tsx
 * const [showDeleteModal, setShowDeleteModal] = useState(false);
 *
 * const handleDelete = () => setShowDeleteModal(true);
 * const handleConfirmDelete = () => {
 *   // Perform delete action
 *   setShowDeleteModal(false);
 * };
 *
 * return (
 *   <>
 *     <Button onClick={handleDelete}>Delete</Button>
 *     <DeleteConfirmationModal
 *       open={showDeleteModal}
 *       onClose={() => setShowDeleteModal(false)}
 *       onConfirm={handleConfirmDelete}
 *       title="Delete Event" // Optional: custom title
 *       message="Are you sure you want to delete this event?" // Optional: custom message
 *       loading={isDeleting} // Optional: show loading state
 *     />
 *   </>
 * );
 * ```
 */
interface DeleteConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  loading?: boolean;
}

export default function DeleteConfirmationModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  loading = false,
}: DeleteConfirmationModalProps) {
  const { t } = useTranslation();

  const defaultTitle = t("common.deleteConfirmation.title");
  const defaultMessage = t("common.deleteConfirmation.message");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title || defaultTitle}
      primaryAction={{
        content: t("common.delete"),
        destructive: true,
        onAction: onConfirm,
        loading,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: onClose,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <p>{message || defaultMessage}</p>
      </Modal.Section>
    </Modal>
  );
}
